import { onError } from '@apollo/client/link/error';
import { isAuthlessPathname, isLogoutPathname } from 'utils/router';
import { logger } from 'modules/logger';

/**
 * Handles GraphQL and network errors, and redirects to /logout on UNAUTHENTICATED
 * for pages that require auth.
 */
export const logoutLink = onError(({ operation, graphQLErrors, networkError }) => {
  const clientName = operation.getContext().clientAwareness?.name ?? 'unknown';
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      const code = extensions?.code;
      // The server forwards BAD_USER_INPUT messages from backend responses, which can echo the request's input.
      // Other codes get a fixed safe message outside local.
      const loggableMessage = code === 'BAD_USER_INPUT' ? '<REDACTED>' : message;
      if (code === 'PERSISTED_QUERY_NOT_FOUND') {
        logger.error(
          `[GraphQL error]: Message: ${message}, Operation: ${operation.operationName}, Client: ${clientName}`
        );
      } else if (code !== 'UNAUTHENTICATED') {
        logger.error(
          `[GraphQL error]: Message: ${loggableMessage}, Code: ${code}, Location: ${JSON.stringify(
            locations
          )}, Path: ${path}, Client: ${clientName}`
        );
      }
    });

    if (networkError) {
      if (networkError.message !== 'Failed to fetch') {
        // These logs will have a linked Error within analytics tools.
        logger.error(`[Network error]: ${networkError.message}, Client: ${clientName}`);
      } else {
        // These can be from CORS, internet instability
        logger.error(`[Unknown Network error]: ${networkError.message}, Client: ${clientName}`);
      }
    }

    const pathname = window.location.pathname;
    // Root-owned queries remain mounted while /logout performs async cleanup. Reloading the same
    // route here can abort that cleanup and trap an expired token in a reload loop.
    const shouldLogout =
      !isLogoutPathname(pathname) &&
      !isAuthlessPathname(pathname) &&
      graphQLErrors.some((error) => error.extensions?.code === 'UNAUTHENTICATED');

    if (shouldLogout) {
      logger.info('[Apollo Client]: Logging out due to receiving UNAUTHENTICATED from GraphQL');
      window.location.href = '/logout';
    }
  }
});
